import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { BarAutoAdBannerContainer } from '../../advertisement/containers/BarAutoAdBannerContainer';
import { useBannerAdListQuery } from '../../advertisement/query/useBannerAdListQuery';
import { Loading } from '../components/Loading';
import { Placeholder } from '../components/Placeholder';
import { ScrollGuide } from '../components/ScrollGuide';
import { Title } from '../components/Title';
import { VerticalProductCardWithAdBannerGridContainer } from '../components/VerticalProductCardWithAdBannerGridContainer';
import { useProductCardUBL } from '../log/useProductCardUBL';
import { useInfiniteRecommendService } from '../services/useInfiniteRecommendService';
import { useProductCardService } from '../services/useProductCardService';
import { useProductCardTypeAdBannerUbl } from '../services/useProductCardTypeAdBannerUbl';
import { useScrollGuideService } from '../services/useScrollGuideService';
import { convertToBarAutoAdBannerIndex } from '../utils/convertToBarAutoAdBannerIndex';
import { isBarAutoAdBannerSlot } from '../utils/isBarAutoAdBannerSlot';

interface Props {
  className?: string;
  isAdBannerEnabled: boolean;
}

export const BasicContainer = ({ className, isAdBannerEnabled }: Props) => {
  const {
    bottom,
    visible: scroll_guide_visible,
    is_revealed: scroll_guide_revealed,
    setVisible: setScrollGuideVisible,
  } = useScrollGuideService();
  const { originalProductId, infiniteRecommend, ref, selectedDrLogMap } = useInfiniteRecommendService();
  const productCardUBL = useProductCardUBL({ original_product_id: originalProductId, selectedDrLogMap });
  const product_card_ubl_handler = useProductCardService({
    ...productCardUBL,
    onImpression: () => {
      if (!scroll_guide_revealed) {
        setScrollGuideVisible(true);
      }
    },
  });

  const {
    product_card_type_ad_banner_items,
    ad_banner_items,
    is_loading: is_ad_banner_loading,
  } = useBannerAdListQuery();
  const product_card_type_ad_banner_ubl_handler = useProductCardTypeAdBannerUbl();

  if (infiniteRecommend.isLoading || is_ad_banner_loading) {
    return (
      <Section className={className} ref={ref}>
        <Title main_title={infiniteRecommend.title} sub_title={infiniteRecommend.subTitle} />
        <Placeholder />
      </Section>
    );
  }

  if (infiniteRecommend.isError || infiniteRecommend.isEmpty) {
    return null;
  }

  return (
    <Section className={className} ref={ref}>
      <Title
        main_title={infiniteRecommend.title}
        sub_title={infiniteRecommend.subTitle}
        is_ad={infiniteRecommend.isAd}
      />
      {scroll_guide_visible && <ScrollGuide bottom={bottom} />}
      <CardList>
        <VerticalProductCardWithAdBannerGridContainer
          useWindowScroll
          increaseViewportBy={200}
          items={infiniteRecommend.list ?? []}
          onEndReached={infiniteRecommend.handleEndReached}
          is_ad_banner_enabled={isAdBannerEnabled}
          ad_banner_items={ad_banner_items}
          product_card_ad_banner_items={product_card_type_ad_banner_items}
          product_card_ubl_handler={product_card_ubl_handler}
          ad_banner_ubl_handler={product_card_type_ad_banner_ubl_handler}
          banner={(_, row_index) => {
            if (row_index === 14 && isAdBannerEnabled) {
              return (
                <TempSection>
                  <TopBorder />
                  <Title
                    main_title='이 상품 어때요?'
                    sub_title='취향을 넓혀줄 아이템이에요'
                    is_ad={infiniteRecommend.isAd}
                  />
                </TempSection>
              );
            }
            if (isBarAutoAdBannerSlot({ row_index }) && isAdBannerEnabled) {
              return (
                <BarAutoAdBannerContainer
                  banner_index={convertToBarAutoAdBannerIndex({ row_index, offset: 1 })}
                  position='inner'
                />
              );
            }
          }}
        />
        {infiniteRecommend.isFetchingNextPage && infiniteRecommend.hasNextPage && <Loading />}
      </CardList>
    </Section>
  );
};

const Section = styled.section`
  padding-top: 5px;
  border-top: 8px solid ${vars.v3Color.gray100};
`;

const TopBorder = styled.div`
  border-top: 8px solid ${vars.v3Color.gray100};
`;

const TempSection = styled.section`
  margin-bottom: -24px;
`;

const CardList = styled.div``;
